import {
  useState,
  useCallback,
  useRef,
  useMemo,
  type CSSProperties,
  type ChangeEvent,
  type DragEvent,
  type SyntheticEvent,
} from 'react'
import {Typography, Box, Tabs, Tab, Button} from '@mui/material'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import {useTranslation} from 'react-i18next'
import {useAppDispatch, useAppSelector} from '../../../common/store'
import {
  setBgMode,
  setBgAngle,
  setBgImageKey,
  setBgImageUrl,
  selectActiveScene,
} from '../../../common/components/store/scenesSlice'
import type {BgMode} from '../../../common/components/store/scenesSlice'
import AnglePicker from '../sharedWidgets/AnglePicker'
import GradientEditor from './GradientEditor'
import {sealUndo} from '../../../common/undoable'
import LoadingLogo from '../LoadingLogo'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'
import {useAuth} from '../../auth/useAuth'
import {uploadAsset} from '../../../common/api/uploadAsset'
import {cleanupReplacedAsset} from '../../../common/api/cleanupReplacedAsset'
import {handleApiError} from '../../../common/errors/handleApiError'
import {ALLOWED_IMAGE_TYPES, MAX_IMAGE_SIZE_BYTES} from '../../../common/constants/assetUpload'
import {trackEvent} from '../../../common/analytics/trackEvent'

// ── Helpers ───────────────────────────────────────────────────────

const loadImageFile = (file: File): Promise<string> =>
  new Promise((resolve) => {
    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result === 'string') resolve(reader.result)
    }
    reader.readAsDataURL(file)
  })

/** Validate image file type and size. Returns error i18n key or null. */
const validateImageFile = (file: File): string | null => {
  if (!ALLOWED_IMAGE_TYPES.has(file.type)) return 'scene.background.invalidType'
  if (file.size > MAX_IMAGE_SIZE_BYTES) return 'scene.background.tooLarge'
  return null
}

// ── FreeBackgroundPanel — flat background settings (no accordion) ─

const FreeBackgroundPanel = () => {
  const {t} = useTranslation()
  const tokens = useAppTokens()
  const dispatch = useAppDispatch()
  const {user} = useAuth()

  const bgMode = useAppSelector((s) => selectActiveScene(s).bgMode)
  const bgAngle = useAppSelector((s) => selectActiveScene(s).bgAngle)
  const bgImageUrl = useAppSelector((s) => selectActiveScene(s).bgImageUrl)
  const bgImageKey = useAppSelector((s) => selectActiveScene(s).bgImageKey)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const handleTabChange = useCallback(
    (_: SyntheticEvent, newValue: number) => {
      const mode: BgMode = newValue === 0 ? 'gradient' : 'image'
      dispatch(setBgMode(mode))
    },
    [dispatch],
  )

  const handleAngleChange = useCallback(
    (angle: number) => {
      dispatch(setBgAngle(angle))
    },
    [dispatch],
  )

  const handleSealUndo = useCallback(() => {
    dispatch(sealUndo())
  }, [dispatch])

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const applyImageFile = useCallback(
    async (file: File) => {
      setUploadError(null)
      const mode = user ? 'paid' : 'free'

      // For authenticated users, upload to S3
      if (user) {
        const error = validateImageFile(file)
        if (error) {
          setUploadError(t(error))
          const reason = error.includes('Type') || error.includes('type') ? 'bad_format' : 'too_large'
          trackEvent('asset_upload_rejected', {kind: 'bg', reason, editor_mode: mode})
          return
        }
        setUploading(true)
        try {
          const {key, url} = await uploadAsset(file)
          cleanupReplacedAsset(bgImageKey)
          dispatch(setBgImageKey(key))
          dispatch(setBgImageUrl(url))
          trackEvent('asset_uploaded', {kind: 'bg', format: file.type, size_bytes: file.size, editor_mode: mode})
        } catch (err) {
          // Surface backend 4xx reasons (e.g. the 413 storage-quota message) instead
          // of a generic error, so the user learns they've hit their quota.
          setUploadError(handleApiError(err, 'uploadBackground') ?? t('scene.background.uploadError'))
        } finally {
          setUploading(false)
        }
        return
      }

      // For anonymous users, use data URL (can't save anyway)
      const dataUrl = await loadImageFile(file)
      dispatch(setBgImageUrl(dataUrl))
      trackEvent('asset_uploaded', {kind: 'bg', format: file.type, size_bytes: file.size, editor_mode: mode})
    },
    [dispatch, user, t, bgImageKey],
  )

  const handleFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return
      await applyImageFile(file)
      e.target.value = ''
    },
    [applyImageFile],
  )

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault()
    setDragOver(true)
  }, [])

  const handleDragLeave = useCallback(() => {
    setDragOver(false)
  }, [])

  const handleDrop = useCallback(
    async (e: DragEvent) => {
      e.preventDefault()
      setDragOver(false)
      const file = e.dataTransfer.files[0]
      if (!file || !file.type.startsWith('image/')) return
      await applyImageFile(file)
    },
    [applyImageFile],
  )

  const tabIndex = bgMode === 'gradient' ? 0 : 1
  const sx = useMemo(() => buildStyles(tokens), [tokens])

  return (
    <Box>
      <Tabs value={tabIndex} onChange={handleTabChange} variant="fullWidth" sx={sx.tabs}>
        <Tab label={t('scene.background.gradient')} sx={sx.tab} />
        <Tab label={t('scene.background.image')} sx={sx.tab} />
      </Tabs>

      {bgMode === 'gradient' && (
        <Box sx={sx.gradientContent}>
          <GradientEditor />
          <Box sx={sx.angleSection}>
            <Typography sx={sx.colorLabel}>{t('scene.background.angle')}</Typography>
            <Box sx={sx.angleRow}>
              <AnglePicker value={bgAngle} onChange={handleAngleChange} onDragEnd={handleSealUndo} />
              <Typography sx={sx.angleValue}>{bgAngle}°</Typography>
            </Box>
          </Box>
        </Box>
      )}

      {bgMode === 'image' && (
        <Box sx={sx.imageContent}>
          <Box
            sx={{...sx.dropZone, ...(dragOver ? sx.dropZoneActive : {})}}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {bgImageUrl ? (
              <Box component="img" src={bgImageUrl} alt={t('a11y.backgroundPreview')} sx={sx.imagePreview} />
            ) : (
              <Box sx={sx.emptyPreview}>
                <UploadFileIcon sx={{fontSize: 24, opacity: 0.4}} />
                <Typography sx={sx.dropText}>{t('scene.background.dropHere')}</Typography>
              </Box>
            )}
            {dragOver && (
              <Box sx={sx.dropOverlay}>
                <Typography sx={sx.dropText}>{t('scene.background.dropHere')}</Typography>
              </Box>
            )}
          </Box>

          <Button
            variant="outlined"
            size="small"
            startIcon={uploading ? <LoadingLogo size={14} /> : <UploadFileIcon />}
            onClick={handleUploadClick}
            disabled={uploading}
            sx={sx.uploadButton}
          >
            {uploading ? t('scene.background.uploading') : t('scene.background.upload')}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFileChange}
            style={{display: 'none'}}
          />
          {uploadError && <Typography sx={sx.errorText}>{uploadError}</Typography>}
        </Box>
      )}
    </Box>
  )
}

const buildStyles = (tokens: ThemeTokens) => ({
  tabs: {
    minHeight: 32,
    marginBottom: '12px',
    '& .MuiTabs-indicator': {
      height: 2,
    },
  },
  tab: {
    minHeight: 32,
    fontSize: 11,
    fontWeight: 500,
    textTransform: 'none' as const,
    color: tokens.text.muted,
    '&.Mui-selected': {
      color: tokens.text.primary,
    },
  },
  gradientContent: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  },
  colorLabel: {
    fontSize: 11,
    color: tokens.text.muted,
    fontWeight: 500,
  },
  angleSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
  },
  angleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  angleValue: {
    fontSize: 13,
    fontWeight: 500,
    color: tokens.text.primary,
    minWidth: 40,
  },
  imageContent: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '10px',
    alignItems: 'stretch',
  },
  dropZone: {
    position: 'relative' as const,
    borderRadius: '6px',
    border: `2px dashed ${tokens.accordion.border}`,
    overflow: 'hidden',
    transition: 'border-color 0.2s',
    cursor: 'pointer',
  },
  dropZoneActive: {
    borderColor: tokens.text.primary,
  },
  imagePreview: {
    width: '100%',
    height: 120,
    objectFit: 'cover' as const,
    display: 'block',
  },
  emptyPreview: {
    width: '100%',
    height: 120,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    color: tokens.text.muted,
  },
  dropOverlay: {
    position: 'absolute' as const,
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(2px)',
  },
  dropText: {
    fontSize: 12,
    fontWeight: 600,
    color: 'rgba(255, 255, 255, 0.9)',
  },
  uploadButton: {
    fontSize: 11,
    textTransform: 'none' as const,
    color: tokens.text.secondary,
    borderColor: tokens.accordion.border,
    '&:hover': {
      borderColor: tokens.text.muted,
    },
  },
  errorText: {
    fontSize: 11,
    color: '#f44336',
  },
  colorInput: {
    width: '100%',
    height: 32,
    border: `1px solid ${tokens.accordion.border}`,
    borderRadius: 6,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    padding: 2,
  } as CSSProperties,
})

export default FreeBackgroundPanel
